import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';
import axios from 'axios';

export const usePeopleFetch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      try {
        // First fetch people data with increased timeout
        const peopleResponse = await apiHandler.post(
          `/intent-leads/${leadId}/people`,
          {},
          {
            timeout: 30000 // Increase timeout to 30 seconds
          }
        );
        
        // Then fetch fresh lead data to get updated state
        const leadResponse = await apiHandler.get(`/intent-leads/clubbed/${leadId}`);
        
        return {
          peopleData: peopleResponse.data,
          leadData: leadResponse.data
        };
      } catch (error) {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          console.error('Request timeout while fetching people data');
          // Set people_fetched to false on timeout so it can be retried
          queryClient.setQueryData(['intentLead', leadId], (old: any) => ({
            ...old,
            company_data: {
              ...old.company_data,
              people_fetched: false
            }
          }));
        }
        throw error;
      }
    },
    onMutate: async (leadId) => {
      // Store previous data for rollback
      const previousData = queryClient.getQueryData(['leads']);
      
      // Optimistically update UI to show loading state
      queryClient.setQueryData(['leads'], (old: any) => {
        if (!old?.pages) return old;
        
        return {
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((lead: any) => {
              if (lead._id === leadId) {
                return {
                  ...lead,
                  company_data: {
                    ...lead.company_data,
                    people_fetched: false
                  }
                };
              }
              return lead;
            })
          }))
        };
      });

      return { previousData };
    },
    onError: (error, leadId, context) => {
      // Revert to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(['leads'], context.previousData);
      }
      
      // Set people_fetched to false so it can be retried
      queryClient.setQueryData(['intentLead', leadId], (old: any) => ({
        ...old,
        company_data: {
          ...old.company_data,
          people_fetched: false
        }
      }));
    },
    onSuccess: async (data, leadId) => {
      const updatedLeadData = {
        ...data.leadData,
        company_data: {
          ...data.leadData.company_data,
          people_fetched: true
        }
      };

      // Update both caches
      queryClient.setQueryData(['intentLead', leadId], updatedLeadData);
      queryClient.setQueryData(['leads'], (old: any) => {
        if (!old?.pages) return old;
        
        return {
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((lead: any) => {
              if (lead._id === leadId) {
                return updatedLeadData;
              }
              return lead;
            })
          }))
        };
      });
    }
  });
}; 